/**
 * Converter utilities for transforming between ReactFlow state and ProcessDefinition JSON.
 */

/**
 * Convert ReactFlow nodes to ProcessNodeSchema format
 */
function convertNodeToSchema(rfNode) {
  const data = rfNode.data;
  
  const nodeSchema = {
    node_id: data.node_id || rfNode.id,
    name: data.name || '',
    description: data.description || '',
    execution_type: data.execution_type || 'llm',
    prompt_template: data.prompt_template || '',
    system_prompt: data.system_prompt || '',
    model: data.model || 'gpt-4o',
    temperature: data.temperature ?? 0.3,
    max_tokens: data.max_tokens || 2048,
    inputs: data.inputs || [],
    outputs: data.outputs || [],
    timeout_seconds: data.timeout_seconds || 120,
    retry_count: data.retry_count ?? 2,
    expect_json: data.expect_json || false,
  };

  if (data.execution_type === 'snn_pipeline' && data.snn_config) {
    const sc = data.snn_config;
    nodeSchema.snn_config = {
      execution_mode: sc.execution_mode || 'BUILD_AND_RUN',
      snn_execution_mode: sc.snn_execution_mode || 'not_compiled',
      max_nodes: sc.max_nodes || 256,
      max_hops: sc.max_hops || 3,
      similarity_top_k: sc.similarity_top_k || 10,
      decompose_query: sc.decompose_query ?? true,
      nl_query_input: sc.nl_query_input || 'nl_query',
      min_similarity: sc.min_similarity ?? 0.3,
      nl_prompt: sc.nl_prompt || '',
    };
    // SEM focus areas: stored as a newline/comma-separated string in the UI;
    // export as a string[] to match the backend SNNPipelineConfig.focus_areas.
    const focusRaw = sc.focus_areas || '';
    const focusList = (Array.isArray(focusRaw)
      ? focusRaw
      : String(focusRaw).split(/[\n,]/))
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .filter((s) => s.length > 0);
    if (focusList.length > 0) {
      nodeSchema.snn_config.focus_areas = focusList;
    }
    if (sc.graph_id) {
      nodeSchema.snn_config.graph_id = sc.graph_id;
      if (sc.graph_name) nodeSchema.snn_config.graph_name = sc.graph_name;
    }
    if (sc.router_graph_id) {
      nodeSchema.snn_config.router_graph_id = sc.router_graph_id;
      if (sc.router_graph_name) nodeSchema.snn_config.router_graph_name = sc.router_graph_name;
      nodeSchema.snn_config.auto_resolve_ambiguous = sc.auto_resolve_ambiguous ?? true;
    }
    if (sc.workspace_id) {
      nodeSchema.snn_config.workspace_id = sc.workspace_id;
    }
    if (sc.pipeline_settings && Object.keys(sc.pipeline_settings).length > 0) {
      nodeSchema.snn_config.pipeline_settings = { ...sc.pipeline_settings };
    }
  }

  if (data.execution_type === 'case_extractor' && data.case_extractor_config) {
    const cc = data.case_extractor_config;
    nodeSchema.case_extractor_config = {
      schema_path: cc.schema_path || '',
      case_text_input: cc.case_text_input || 'case_text',
      case_id_input: cc.case_id_input || 'case_id',
      domain_input: cc.domain_input || 'domain',
      extra_inputs: Array.isArray(cc.extra_inputs) ? cc.extra_inputs : [],
      llm_model: cc.llm_model || 'gpt-4o',
      temperature: cc.temperature ?? 0.0,
      max_tokens: cc.max_tokens || 12000,
      timeout_seconds: cc.timeout_seconds || 480,
      cache_by_hash: cc.cache_by_hash ?? true,
    };
  }

  if (data.execution_type === 'http' && data.http_config) {
    const hc = data.http_config;
    nodeSchema.http_config = {
      method: (hc.method || 'GET').toUpperCase(),
      url: hc.url || '',
      headers: hc.headers && typeof hc.headers === 'object' ? { ...hc.headers } : {},
      query_params: hc.query_params && typeof hc.query_params === 'object'
        ? { ...hc.query_params } : {},
      body_mode: hc.body_mode || 'none',
      body_json: hc.body_json ?? null,
      body_raw: hc.body_raw ?? null,
      body_form: hc.body_form && typeof hc.body_form === 'object'
        ? { ...hc.body_form } : null,
      auth_type: hc.auth_type || 'none',
      auth_credential_ref: hc.auth_credential_ref || null,
      auth_param_name: hc.auth_param_name || null,
      expected_status_codes: Array.isArray(hc.expected_status_codes) && hc.expected_status_codes.length
        ? hc.expected_status_codes.map((n) => parseInt(n, 10)).filter(Number.isFinite)
        : [200, 201, 202, 204],
      response_mode: hc.response_mode || 'json',
      follow_redirects: hc.follow_redirects !== false,
      verify_tls: hc.verify_tls !== false,
    };
    // Strip top-level LLM defaults that don't apply to HTTP nodes so the
    // backend's prompt_template validator stays happy.
    nodeSchema.prompt_template = '';
    nodeSchema.system_prompt = '';
    nodeSchema.model = '';
  }

  if (data.execution_type === 'python' && data.python_config) {
    const pc = data.python_config;
    nodeSchema.python_config = {
      code: pc.code || '',
      requirements: Array.isArray(pc.requirements) ? [...pc.requirements] : [],
      entrypoint: pc.entrypoint || 'main',
      cpu_seconds: parseInt(pc.cpu_seconds ?? 30, 10),
      wall_seconds: parseInt(pc.wall_seconds ?? 60, 10),
      memory_mb: parseInt(pc.memory_mb ?? 512, 10),
      network_access: !!pc.network_access,
      mcp_servers: Array.isArray(pc.mcp_servers) ? [...pc.mcp_servers] : [],
    };
    nodeSchema.prompt_template = '';
    nodeSchema.system_prompt = '';
    nodeSchema.model = '';
  }

  if (data.execution_type === 'cross_link' && data.cross_link_config) {
    const cl = data.cross_link_config;
    const targetType = cl.target_type || 'process';
    const cfg = {
      target_type: targetType,
      nl_query_input: cl.nl_query_input || 'nl_query',
      propagate_workspace: cl.propagate_workspace !== false,
      auto_resolve_ambiguous: cl.auto_resolve_ambiguous ?? true,
    };
    if (targetType === 'process') {
      cfg.target_process_id = cl.target_process_id || null;
      if (cl.target_process_name) cfg.target_process_name = cl.target_process_name;
    } else if (targetType === 'graph') {
      cfg.graph_id = cl.graph_id || null;
      if (cl.graph_name) cfg.graph_name = cl.graph_name;
    } else if (targetType === 'router') {
      cfg.router_graph_id = cl.router_graph_id || null;
      if (cl.router_graph_name) cfg.router_graph_name = cl.router_graph_name;
    }
    if (cl.workspace_id) cfg.workspace_id = cl.workspace_id;
    if (cl.input_mapping && typeof cl.input_mapping === 'object'
        && Object.keys(cl.input_mapping).length > 0) {
      cfg.input_mapping = { ...cl.input_mapping };
    }
    if (cl.output_mapping && typeof cl.output_mapping === 'object'
        && Object.keys(cl.output_mapping).length > 0) {
      cfg.output_mapping = { ...cl.output_mapping };
    }
    if (cl.output_prefix) cfg.output_prefix = cl.output_prefix;
    nodeSchema.cross_link_config = cfg;
    nodeSchema.prompt_template = '';
    nodeSchema.system_prompt = '';
    nodeSchema.model = '';
  }

  if (data.execution_type === 'chain_reasoner' && data.chain_reasoner_config) {
    const cr = data.chain_reasoner_config;
    const isMulti = !!cr.pack_registry_path || cr.mode === 'multi_pack';
    const cfg = {
      resolver: cr.resolver || 'snn_pipeline',
      pipeline_run_url: cr.pipeline_run_url || 'http://127.0.0.1:8000/api/pipeline/run',
      case_evidence_input: cr.case_evidence_input || 'case_evidence_frame',
      case_text_input: cr.case_text_input || 'case_text',
      domain_filter: Array.isArray(cr.domain_filter) ? cr.domain_filter : [],
      max_iterations_per_pack: cr.max_iterations_per_pack ?? 200,
      require_at_least_one_case_predicate_per_chain:
        cr.require_at_least_one_case_predicate_per_chain ?? true,
      near_coverage_delta: cr.near_coverage_delta ?? 1,
      timeout_seconds: cr.timeout_seconds || 720,
    };
    if (isMulti) {
      cfg.pack_registry_path = cr.pack_registry_path || '';
      cfg.manifest_path = cr.manifest_path || '';
      cfg.decision_policy_path = cr.decision_policy_path || '';
    } else {
      cfg.pack_path = cr.pack_path || '';
      if (cr.manifest_path) cfg.manifest_path = cr.manifest_path;
    }
    nodeSchema.chain_reasoner_config = cfg;
  }

  if (data.is_output) {
    nodeSchema.is_output = true;
  }

  return nodeSchema;
}

/**
 * Convert ReactFlow edge to ProcessSynapse format
 */
function convertEdgeToSynapse(rfEdge) {
  const data = rfEdge.data || {};
  
  return {
    synapse_id: rfEdge.id || `syn_${Date.now()}`,
    source_node: rfEdge.source,
    target_node: rfEdge.target,
    output_mapping: data.outputMapping || {},
    condition: data.condition || null,
    weight: data.weight ?? 1.0,
    merge_strategy: data.mergeStrategy || 'replace',
  };
}

/**
 * Export ReactFlow state to ProcessDefinition JSON
 */
export function exportToProcessJSON(nodes, edges, gates, metadata, graphId) {
  // Filter out gate nodes from the process nodes
  // Gates on canvas are represented separately
  const processNodes = nodes
    .filter(n => n.type !== 'gateNode')
    .map(convertNodeToSchema);

  // Convert gates on canvas to gate definitions
  const canvasGates = nodes
    .filter(n => n.type === 'gateNode')
    .map(n => ({
      gate_id: n.data.gate_id || n.id,
      gate_type: n.data.gate_type || 'AND',
      input_nodes: n.data.input_nodes || [],
      output_nodes: n.data.output_nodes || [],
      threshold: n.data.threshold,
      confidence_threshold: n.data.confidence_threshold,
      low_confidence_nodes: n.data.low_confidence_nodes || [],
      description: n.data.description || '',
    }));

  // Combine with gates defined in properties panel
  const allGates = [...gates, ...canvasGates];

  // Convert edges
  const synapses = edges.map(convertEdgeToSynapse);

  // Process-wide SEM focus areas: edited as a newline/comma string, stored
  // as a string[] to match the backend ProcessDefinition.focus_areas.
  const focusRaw = metadata.focusAreas || '';
  const focusList = (Array.isArray(focusRaw)
    ? focusRaw
    : String(focusRaw).split(/[\n,]/))
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0);

  const result = {
    name: metadata.name || 'Unnamed Process',
    description: metadata.description || '',
    nodes: processNodes,
    synapses,
    gates: allGates,
    max_parallel: metadata.maxParallel || 5,
    global_timeout_seconds: metadata.globalTimeoutSeconds || 600,
    graph_id: graphId || null,
    tags: metadata.tags || [],
  };
  if (focusList.length > 0) {
    result.focus_areas = focusList;
  }
  return result;
}

/**
 * Convert ProcessNodeSchema to ReactFlow node
 */
function convertSchemaToNode(nodeSchema, index) {
  // The API exposes the neutral wire name 'runtime_pipeline' for SNN / Platform
  // Pipeline nodes (see utils/vocabulary.js ENUM_VALUE_MAP). The builder works
  // internally with 'snn_pipeline', so normalize on import; otherwise these
  // nodes silently fall through to the default 'llmNode' and never render as
  // Platform Pipeline nodes.
  const execType = nodeSchema.execution_type === 'runtime_pipeline'
    ? 'snn_pipeline'
    : nodeSchema.execution_type;
  // The neutral response also carries the SNN config under 'runtime_config';
  // accept either so the graph binding/config survives the round-trip.
  const snnConfig = nodeSchema.snn_config || nodeSchema.runtime_config || null;

  // Determine node type based on execution_type
  let nodeType = 'llmNode';
  if (execType === 'snn_pipeline') {
    nodeType = 'snnPipelineNode';
  } else if (execType === 'case_extractor') {
    nodeType = 'caseExtractorNode';
  } else if (execType === 'chain_reasoner') {
    nodeType = 'chainReasonerNode';
  } else if (execType === 'http') {
    nodeType = 'httpNode';
  } else if (execType === 'python') {
    nodeType = 'pythonNode';
  } else if (execType === 'cross_link') {
    nodeType = 'crossLinkNode';
  }

  // Calculate position based on index (simple grid layout)
  const row = Math.floor(index / 3);
  const col = index % 3;
  const position = {
    x: 100 + col * 300,
    y: 100 + row * 200,
  };

  return {
    id: nodeSchema.node_id,
    type: nodeType,
    position,
    data: {
      node_id: nodeSchema.node_id,
      name: nodeSchema.name,
      description: nodeSchema.description,
      execution_type: execType,
      prompt_template: nodeSchema.prompt_template,
      system_prompt: nodeSchema.system_prompt,
      model: nodeSchema.model,
      temperature: nodeSchema.temperature,
      max_tokens: nodeSchema.max_tokens,
      inputs: nodeSchema.inputs,
      outputs: nodeSchema.outputs,
      timeout_seconds: nodeSchema.timeout_seconds,
      retry_count: nodeSchema.retry_count,
      expect_json: nodeSchema.expect_json,
      snn_config: snnConfig
        ? {
            ...snnConfig,
            // focus_areas is stored as string[] in the definition but edited
            // as a newline-separated string in the properties panel.
            focus_areas: Array.isArray(snnConfig.focus_areas)
              ? snnConfig.focus_areas.join('\n')
              : snnConfig.focus_areas || '',
          }
        : snnConfig,
      case_extractor_config: nodeSchema.case_extractor_config,
      chain_reasoner_config: nodeSchema.chain_reasoner_config,
      http_config: nodeSchema.http_config,
      python_config: nodeSchema.python_config,
      cross_link_config: nodeSchema.cross_link_config,
      is_output: !!nodeSchema.is_output,
    },
  };
}

/**
 * Convert ProcessSynapse to ReactFlow edge
 */
function convertSynapseToEdge(synapse) {
  return {
    id: synapse.synapse_id,
    source: synapse.source_node,
    target: synapse.target_node,
    type: 'smoothstep',
    animated: false,
    data: {
      outputMapping: synapse.output_mapping || {},
      condition: synapse.condition || '',
      weight: synapse.weight ?? 1.0,
      mergeStrategy: synapse.merge_strategy || 'replace',
    },
  };
}

/**
 * Import ProcessDefinition JSON to ReactFlow state
 */
export function importFromProcessJSON(processJSON) {
  // Handle both full process definition and just the definition object
  const definition = processJSON.definition || processJSON;
  
  // Convert nodes
  const nodes = (definition.nodes || []).map((node, index) => 
    convertSchemaToNode(node, index)
  );

  // Convert synapses to edges
  const edges = (definition.synapses || []).map(convertSynapseToEdge);

  // Extract gates
  const gates = (definition.gates || []).map(gate => ({
    gate_id: gate.gate_id,
    gate_type: gate.gate_type,
    input_nodes: gate.input_nodes || [],
    output_nodes: gate.output_nodes || [],
    threshold: gate.threshold,
    confidence_threshold: gate.confidence_threshold,
    low_confidence_nodes: gate.low_confidence_nodes || [],
    description: gate.description || '',
  }));

  // Extract metadata
  const rawFocus = processJSON.focus_areas || definition.focus_areas;
  const metadata = {
    name: processJSON.name || definition.name || '',
    description: processJSON.description || definition.description || '',
    maxParallel: processJSON.max_parallel || definition.max_parallel || 5,
    globalTimeoutSeconds: processJSON.global_timeout_seconds || definition.global_timeout_seconds || 600,
    tags: processJSON.tags || definition.tags || [],
    // focus_areas is stored as string[] but edited as a newline string.
    focusAreas: Array.isArray(rawFocus) ? rawFocus.join('\n') : (rawFocus || ''),
  };

  return {
    nodes,
    edges,
    gates,
    metadata,
  };
}

/**
 * Auto-layout nodes in a left-to-right flow
 */
export function autoLayoutNodes(nodes, edges) {
  // Build adjacency list
  const outgoing = {};
  const incoming = {};
  
  edges.forEach(edge => {
    if (!outgoing[edge.source]) outgoing[edge.source] = [];
    if (!incoming[edge.target]) incoming[edge.target] = [];
    outgoing[edge.source].push(edge.target);
    incoming[edge.target].push(edge.source);
  });

  // Find entry nodes (no incoming edges)
  const entryNodes = nodes.filter(n => !incoming[n.id] || incoming[n.id].length === 0);

  // BFS to assign layers
  const layers = {};
  const visited = new Set();
  let queue = entryNodes.map(n => ({ id: n.id, layer: 0 }));

  while (queue.length > 0) {
    const { id, layer } = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    
    if (!layers[layer]) layers[layer] = [];
    layers[layer].push(id);

    const targets = outgoing[id] || [];
    targets.forEach(targetId => {
      if (!visited.has(targetId)) {
        queue.push({ id: targetId, layer: layer + 1 });
      }
    });
  }

  // Position nodes based on layers
  const NODE_WIDTH = 250;
  const NODE_HEIGHT = 150;
  const LAYER_GAP = 350;
  const NODE_GAP = 180;

  const layoutedNodes = nodes.map(node => {
    // Find which layer this node is in
    let nodeLayer = 0;
    let indexInLayer = 0;
    
    for (const [layer, nodeIds] of Object.entries(layers)) {
      const idx = nodeIds.indexOf(node.id);
      if (idx !== -1) {
        nodeLayer = parseInt(layer);
        indexInLayer = idx;
        break;
      }
    }

    const layerNodeCount = layers[nodeLayer]?.length || 1;
    const layerHeight = layerNodeCount * NODE_GAP;
    const startY = (layerHeight / 2) - NODE_HEIGHT / 2;

    return {
      ...node,
      position: {
        x: 100 + nodeLayer * LAYER_GAP,
        y: 100 + indexInLayer * NODE_GAP - startY / 2,
      },
    };
  });

  return layoutedNodes;
}

export default {
  exportToProcessJSON,
  importFromProcessJSON,
  autoLayoutNodes,
};
